
import { Component, EventEmitter, Inject, NgZone, OnInit, Output, ViewChild, DOCUMENT } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { AlertController, IonNav, MenuController, ModalController, NavController, Platform } from '@ionic/angular/lazy';
import { AlertButton, AlertOptions } from '@ionic/core';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map, skip, take, tap, shareReplay } from 'rxjs/operators';
import { Environment } from 'src/environments/environment';
import { AlertService } from './services/alert.service';
import { AnalyticsService } from './services/analytics.service';
import { DeviceService } from './services/device.service';
import { EventsService } from './services/events.service';
import { GlobalConstService } from './services/global-const.service';
import { HandlerErrorService } from './services/handler-error.service';
import { InitialConfigService } from './services/inital-config.service';
import { LanguageService } from './services/language.service';
import { LinksService } from './services/links.service';
import { LoadingService } from './services/loading.service';
import { LoginService } from './services/login.service';
import { MessagesService } from './services/messages.service';
import { NetworkService } from './services/network.service';
import { PlatformService } from './services/platform.service';
import { NotificationService } from './services/notification.service';
import { ToastService } from './services/toast.service';
import { SplashScreen } from '@capacitor/splash-screen';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { ActionPerformed, PermissionStatus, PushNotifications, PushNotificationSchema } from '@capacitor/push-notifications';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SimulationService } from './services/simulation.service';
import { ClientService } from './services/client.service';
import { WooCommerceService } from './services/woocommerce.service';
import { register } from 'swiper/element/bundle';

register();

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit {
  @ViewChild(IonNav) nav: IonNav;
  @Output() exit: EventEmitter<any> = new EventEmitter();
  public pages: any;
  public sections = { documents: false, chat: false, directory: false };
  public userData: any;
  public loggedClientheader = null;
  public breadcrumb$: Observable<any>;

  public isMenuAvailable: boolean = false;
  public sideMenuEnabled: boolean = this.resolveSideMenuEnabled();
  public notificationsPanelEnabled: boolean = this.resolveNotificationsPanelEnabled();

  public buttonSelected: string = 'notifications';

  public isNaN = isNaN;

  public desktop$$ = new BehaviorSubject(null);

  public unreadNotifications$: Observable<number>;

  constructor(
    public platform: Platform,
    public translate: TranslateService,
    public gConst: GlobalConstService,
    public events: EventsService,
    private loading: LoadingService,
    public menuCtrl: MenuController,
    public initialConfig: InitialConfigService,
    private pushNotifications: NotificationService,
    private loginService: LoginService,
    private navCtrl: NavController,
    public alertCtrl: AlertController,
    private toast: ToastService,
    public messageProvider: MessagesService,
    private network: NetworkService,
    private handlerErrorProvider: HandlerErrorService,
    private analyticsService: AnalyticsService,
    private router: Router,
    private route: ActivatedRoute,
    private linksService: LinksService,
    private languageService: LanguageService,
    private platformService: PlatformService,
    private zone: NgZone,
    private initialConfigService: InitialConfigService,
    public globalConst: GlobalConstService,
    public alertService: AlertService,
    public modalController: ModalController,
    public deviceService: DeviceService,
    public simulationService: SimulationService,
    public clientService: ClientService,
    private woocommerceService: WooCommerceService,
    @Inject(DOCUMENT) private document: Document
  ) {
    // this language will be used as a fallback when a translation isn't found in the current language
    translate.setDefaultLang(Environment.DEFAULT_LANGUAGE);

    // the lang to use, if the lang isn't available, it will use the current loader to get them
    translate.use(Environment.DEFAULT_LANGUAGE);
  }

  ngOnInit() {
    this.initializeApp();
  }

  private subscribeSimulationMode() {
    this.route.queryParams
      .pipe(
        filter(({ simulation }) => Boolean(simulation)),
        map(({ simulation }) => simulation === 'true')
      )
      .subscribe((simulation) => {
        if (simulation === true) {
          this.simulationService.activateSimulationMode();
        } else {
          this.simulationService.deactivateSimulationMode();
        }
      });
  }

  private async initializeApp() {
    await SplashScreen.show({
      autoHide: false
    });

    this.subscribeDeepLinks();
    this.subscribeSimulationMode();

    this.desktop$$.next(this.platformService.isWeb());

    await this.platform.ready();
    
    // Configurar StatusBar para Android
    if (this.platform.is('android')) {
      await this.configureStatusBar();
    }
    
    await this.platformService.loadDevice();
    this.setLanguages();
    this.menuCtrl.enable(false);
    const isWindows = this.isWindows();

    if (isWindows) {
      this.document.documentElement.classList.remove('plt-mobile');
      this.document.documentElement.classList.add('plt-desktop');
      this.menuCtrl.enable(true);
    }

    if (!this.network.online()) {
      this.router.navigate(['no-network']);
    } else {
      this.prepareClient();
    }
  }

  private async configureStatusBar() {
    try {
      // Configurar el StatusBar para que no se superponga con el contenido
      await StatusBar.setOverlaysWebView({ overlay: false });
      await StatusBar.setStyle({ style: Style.Light });
      await StatusBar.setBackgroundColor({ color: '#ffffff' });
    } catch (error) {
      console.log('Error configuring status bar:', error);
    }
  }

  isWindows(): boolean {
    if (!window || !navigator) {
      return false;
    }
    const a: string = navigator.userAgent || navigator.vendor || (window as any).opera;

    return /windows/i.test(a);
  }

  get loginPage() {
    console.log('loginPage');

    return Environment.platform === 'web' ? 'login/web' : 'login/first';
  }

  public isLoggedIn(): boolean {
    return !!(this.userData && this.userData.account?.showProfile !== false);
  }

  public goToLogin() {
    this.router.navigate([this.loginPage]);
  }

  get initialPage() {
    const openedClient = this.clientService.isOpenedAccountClient();
    let userData = this.loginService.autoLogin();
    
    // Verificar si estamos en una ruta de login - si es así, no redirigir
    const currentUrl = this.router.url;
    const isLoginRoute = currentUrl.indexOf('/login/first') !== -1 || currentUrl.indexOf('/login/web') !== -1;
    
    // Si estamos en una ruta de login y el cliente tiene opened: true, mantenernos en login
    if (isLoginRoute && openedClient) {
      return currentUrl.startsWith('/') ? currentUrl.substring(1) : currentUrl;
    }

    if (openedClient) {
      if (userData && userData.account && userData.account.showProfile === true) {
        return 'home';
      }
      return 'home-public';
    }

    if (!openedClient) {
      if (!userData || (userData.account && userData.account.showProfile === false)) {
        return this.loginPage;
      }
      return 'home';
    }

    return 'home';
  }

  prepareClient() {
    const clientId = Environment.CLIENT_ID;

    if (clientId === null) {
      this.clientService.saveAppClient(false);
      this.configureApp();
      return;
    }

    const client = this.clientService.getClient();

    client.subscribe(async (client) => {
      console.log('Cliente recibido:', client);
      console.log('Cliente opened:', client.opened);
      
      // Verificar si hay un usuario anónimo logado pero el cliente ya no está abierto
      const userData = this.loginService.autoLogin();
      if (userData && userData.account.name === 'ANONYMOUS' && !client.opened) {
        console.log('Usuario anónimo detectado pero cliente cerrado, limpiando sesión...');
        await this.clearSession();
        this.clientService.saveAppClient(false);
        this.configureApp();
        return;
      }

      this.clientService.saveAppClient(client.opened);
      this.initialConfig.setClientFeatures(client.features);
      this.configureApp();
      
      // Asegurar que los estilos se carguen tanto si el cliente está abierto como cerrado
      if (client.style) {
        console.log('Aplicando estilos del cliente...');
        this.prepareStyles(client.style);
      } else {
        console.log('No hay estilos del cliente disponibles');
      }
    });
  }

  prepareStyles(clientStyles) {
    console.log('prepareStyles ejecutándose...');
    console.log('clientStyles:', clientStyles);
    
    if (clientStyles === null) {
      console.log('clientStyles es null, no se aplican estilos');
      return;
    }

    console.log('Guardando y aplicando estilos del cliente...');
    this.clientService.saveAppTheme(clientStyles);
    this.clientService.setThemeApplication(clientStyles);
    console.log('Estilos aplicados correctamente');
  }

  public configureApp() {
    try {
      console.log('configureApp ejecutándose...');
      
      // Verificar si hay estilos guardados y aplicarlos si es necesario
      const savedStyles = this.clientService.rescueAppStyleSheet();
      if (savedStyles) {
        console.log('Estilos guardados encontrados, aplicándolos...');
        this.clientService.setThemeApplication(savedStyles);
      } else {
        console.log('No hay estilos guardados disponibles');
      }
      
      this.pushNotifications.initializePushNotifications();

      this.subscribeSessionReady();
      this.subscribeRouterChanges();
      this.languageService.subscribeLangChange();
      this.subscribeSessionExpired();
      this.subscribeNotifications();
      this.subscribeNetworkConnectionLost();
      this.subscribeLogoutRequest();

      const params = this.catchTokenFromUrlIfExists();
      const openedClient = this.clientService.isOpenedAccountClient();
      let userData = null;

      // Si existe token en la URL
      if (params !== null) {
        // Logamos con ese token
        this.loginWithUrlParams(params);
        return;
      }

      if (params === null) {
        userData = this.loginService.autoLogin();

        if (!userData) {
          if (Environment.CLIENT_ID !== null) {
            if (openedClient) {
              this.anonymousLogin();
              return;
            }

            this.router.navigate([this.initialPage]).then(() => {
              SplashScreen.hide();
              //this.loading.hide();
            });
            return;
          }

          this.router.navigate([this.loginPage]).then(() => {
            SplashScreen.hide();
            //this.loading.hide();
          });

          return;
        }
        // Verificar y renovar token si es necesario antes de verificar sesiones activas
        this.loginService.checkAndRenewToken().then((tokenValid) => {
          if (!tokenValid) {
            console.log('Token no válido o no se pudo renovar');
            this.toast.info('YOUR-SESSION-HAS-EXPIRED');
            this.logout(true);
            SplashScreen.hide();
            return;
          }

          // Obtener las sesiones activas después de verificar/renovar el token
          this.loginService.getActiveSessions().subscribe((sessions) => {
            if (!sessions) {
              console.log('al logout sin sesiones');
              this.toast.info('YOUR-SESSION-HAS-EXPIRED');
              this.logout(true);
              SplashScreen.hide();
              return; // Detenemos la ejecución aquí
            }

            // Si hay sesiones, continuar con el flujo
            if (openedClient && !userData.account.showProfile) {
              this.router.navigate([this.initialPage]).then(() => {
                SplashScreen.hide();
                this.loading.hide();
              });
            }

            // Continuamos con la configuración de la sesión
            const request$ = this.loginService.configureSession(userData);

            request$.subscribe({
              next: () => {
                // Iniciar monitoreo de tokens cuando la sesión esté lista
                this.loginService.startTokenMonitoring();
              },
              error: async (err) => await this.clearSession(),
              complete: () => {}
            });
          });
        }).catch((error) => {
          console.error('Error verificando token:', error);
          this.toast.info('YOUR-SESSION-HAS-EXPIRED');
          this.logout(true);
          SplashScreen.hide();
        });
      }
    } catch (e) {
      console.error('configureApp error -->', e);
    }
  }

  private anonymousLogin() {
    const clientId = Environment.CLIENT_ID;
    const request = this.loginService.loginAnonymous(clientId);

    return request.pipe(tap(() => (this.initialConfig.homePageLoaded = false))).subscribe({
      next: async (response: any) => {
        console.log('Iniciando login anónimo...');
        
        this.initialConfigService.saveUserData(response);
        this.loginService.saveUserSession(response);

        this.initialConfigService.rootPageComponents = response.rootPageComponents;
        this.initialConfigService.styleSheet = response.client.style;

        await this.loginService.loginWithToken(response.account.firebaseToken);
        await this.getThemeApp();

        console.log('Login anónimo completado, publicando evento session ready');
        this.events.publish({ name: 'session', value: 'ready' });
        
        // Asegurar que el menú esté deshabilitado inmediatamente
        this.menuCtrl.enable(false);
        console.log('Menú deshabilitado para usuario anónimo');
        
        // Asegurar que el loading se oculte después de un breve delay
        setTimeout(() => {
          this.loading.hide();
          console.log('Loading ocultado después del login anónimo');
        }, 100);
        
        // Resetear el flag de homePageLoaded para forzar recarga de componentes
        this.initialConfigService.homePageLoaded = false;
        console.log('homePageLoaded reseteado para forzar recarga de componentes');
      },
      error: (error) => {
        console.log('Anon login failed so redirect to login page:', error);
        this.router.navigate([this.loginPage]).then(() => SplashScreen.hide());
      }
    });
  }

  private catchTokenFromUrlIfExists() {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    if (!urlParams.has('sid') && !urlParams.has('sessionToken')) {
      return null;
    }

    const sessionToken = urlParams.has('sessionToken') ? urlParams.get('sessionToken') : urlParams.get('sid');
    const source = urlParams.has('utm_source') ? urlParams.get('utm_source') : null;

    return {
      sessionToken: sessionToken,
      sid: sessionToken
    };
  }

  private async loginWithUrlParams(params) {
    await this.clearSession();

    const source = params.source;

    this.loginService.saveUserSession({ account: { sessionToken: params.sessionToken } });

    this.loginService
      .updateSession({ source: source })
      .pipe(take(1))
      .subscribe({
        next: async (response: any) => {
          this.initialConfigService.saveUserData(response);
          this.loginService.saveUserSession(response);
          this.globalConst.userData = response;
          this.initialConfigService.rootPageComponents = response.rootPageComponents;
          this.initialConfigService.styleSheet = response.client.style;

          await this.loginService.loginWithToken(response.account.firebaseToken);
          await this.getThemeApp();

          this.events.publish({ name: 'session', value: 'ready' });
          this.router.navigate([window.location.pathname]).then(() => {
            SplashScreen.hide();
            this.loading.hide();
          });
        },
        error: async (err) => {
          await this.loading.hide();
        }
      });
  }

  private getThemeApp() {
    let cssStyleSheet = this.initialConfigService.styleSheet;
    if (!cssStyleSheet) {
      return;
    }
    this.clientService.saveAppTheme(cssStyleSheet);
    this.clientService.setThemeApplication(cssStyleSheet);
  }

  public subscribeNetworkConnectionLost() {
    this.network.checkConnection();
    this.network.connected$$.pipe(filter((connected) => connected === false)).subscribe(() => this.router.navigate(['no-network']));
    this.network.connected$$
      .pipe(
        skip(1),
        filter((connected) => connected === true)
      )
      .subscribe(() => {
        this.router.navigate(['home']);
      });
  }

  private detectAppClose() {
    const userData = this.loginService.rescueSessionDataUser();
    if (!userData) return;

    this.platform.pause.subscribe(() => this.loginService.changeUserStatusInFirebase(userData.account.id, false));
    this.platform.resume.subscribe(() => this.loginService.changeUserStatusInFirebase(userData.account.id, true));
  }

  private configureSections() {
    if (!this.userData) {
      return;
    }

    // if client not has chats deactivate chat rooms calls to the API
    if (this.sections.chat === true) {
      this.messageProvider.getListOfRooms();
    }

    this.initialConfigService.opened.next(this.userData.client.opened);
  }

  private async setLanguages() {
    this.languageService.change(null);
    moment.locale(Environment.DEFAULT_LANGUAGE);
    moment.updateLocale(Environment.DEFAULT_LANGUAGE, {
      months: await this.languageService.getMonthsTranslate()
    });
  }

  private subscribeSessionExpired() {
    this.events.subscribe({ name: 'session', value: 'expired' }).subscribe(async (event) => {
      await this.clearSession();
      this.toast.info('YOUR-SESSION-HAS-EXPIRED');
      // Si existiera por la muerte de alguna petición
      // await this.loading.hide();
      this.router.navigate([this.loginPage]);
    });
  }

  private subscribeRouterChanges() {
    this.breadcrumb$ = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((e: NavigationEnd) => e.url),
      map((e) => e.split('/').filter((p) => p.length > 0)),
      tap(() => this.events.pages$$.next(null))
    );

    // Añadir/quitar clase 'login-page' al body según la ruta
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      const url = event.url.toLowerCase();
      const isLoginPage = url.includes('/login') || url.includes('/register') || url.includes('/rescue');
      
      if (isLoginPage) {
        this.document.body.classList.add('login-page');
      } else {
        this.document.body.classList.remove('login-page');
      }
    });

    this.events.nav.subscribe((data) => {
      if (!data || !data.route) return;

      this.navCtrl.navigateRoot([data.route]);

      if (data.refreshHome) {
        this.initialConfig.homePageLoaded = false;
      }
    });
  }

  private subscribeSessionReady() {
    this.events.subscribe({ name: 'session', value: 'ready' }).subscribe((events) => {
      this.userData = this.loginService.rescueSessionDataUser();
      
      // Configurar las clases del body basado en el tipo de usuario
      if (this.userData.account.showProfile === false) {
        // Usuario anónimo: añadir not-logged y quitar logged
        this.document.body.classList.add('not-logged');
        this.document.body.classList.remove('logged');
      } else {
        // Usuario logado: añadir logged y quitar not-logged
        this.document.body.classList.add('logged');
        this.document.body.classList.remove('not-logged');
      }

      this.pushNotifications.getToken();
      this.pages = this.userData.rootPages.menu;
      this.loggedClientheader = this.userData.client.style.header;

      if (this.userData.client.sections) {
        const { documents, chat, directory } = this.userData.client.sections;
        this.sections = { documents, chat, directory };
      } else {
        // Si no hay sections, usar valores por defecto
        this.sections = { documents: false, chat: false, directory: false };
      }

      this.sideMenuEnabled = this.userData.client.features?.sideMenu !== false;
      this.notificationsPanelEnabled = this.userData.client.features?.notificationsPanel !== false;

      this.detectAppClose();
      this.configureSections();

      // Configurar el menú inmediatamente basado en el tipo de usuario
      if (this.userData.account.showProfile === false) {
        this.menuCtrl.enable(false);
        console.log('Usuario anónimo detectado, menú deshabilitado');
      } else {
        this.menuCtrl.enable(true);
        console.log('Usuario logado detectado, menú habilitado');
      }

      const homePage = this.userData.rootPages.home;
      SplashScreen.hide();
      //this.loading.hide('app component 7');
      this.alertService.showAlerts();

      if (homePage.pageId) {
        this.setHomePage(homePage.pageId);
      } else {
        this.handlerErrorProvider.cmsError();
      }

      this.getNotifications();
      this.askNotificationPermission();
    });
  }

  private async askNotificationPermission() {
    if (this.platformService.isMobile()) {
      const permissions = await PushNotifications.checkPermissions();
      const permissionsStatus: PermissionStatus = await PushNotifications.requestPermissions();
    }
  }

  private setHomePage(pageId) {
    this.initialConfig
      .getHomeConfig(pageId)
      .pipe(take(1))
      .subscribe({
        next: (rootPageComponents) => {
          this.initialConfig.rootPageComponents = rootPageComponents;

          if (this.userData.account.showProfile === false) {
            // Asegurar que el menú esté deshabilitado para usuarios anónimos
            this.menuCtrl.enable(false);
            
            // Navegar a home-public para usuarios anónimos
            // Verificar si ya estamos en home-public para evitar navegación innecesaria
            const currentUrl = this.router.url;
            // No redirigir si estamos en login/first o login/web (usando indexOf para ser más flexible)
            const isLoginRoute = currentUrl.indexOf('/login/first') !== -1 || currentUrl.indexOf('/login/web') !== -1;
            
            console.log('setHomePage - currentUrl:', currentUrl, 'isLoginRoute:', isLoginRoute);
            
            if (!isLoginRoute && (currentUrl.indexOf('login') !== -1 || currentUrl === '/' || currentUrl.indexOf('home-public') === -1)) {
              console.log('Navegando a home-public para usuario anónimo');
              this.router.navigate(['home-public']).then(() => {
                console.log('Navegación a home-public completada');
                // Publicar el evento después de la navegación para asegurar que home-public lo reciba
                setTimeout(() => {
                  this.events.publish({ name: 'anonApp', value: 'ready' });
                  console.log('Evento anonApp:ready publicado');
                }, 100);
              }).catch((error) => {
                console.error('Error navegando a home-public:', error);
              });
            } else if (isLoginRoute) {
              // Si estamos en una ruta de login, no navegar, solo publicar el evento
              console.log('Usuario anónimo en ruta de login, no redirigiendo a home-public. URL:', currentUrl);
              this.events.publish({ name: 'anonApp', value: 'ready' });
            } else {
              // Si ya estamos en home-public, publicar el evento inmediatamente
              this.events.publish({ name: 'anonApp', value: 'ready' });
              console.log('Evento anonApp:ready publicado (ya en home-public)');
            }
          } else {
            this.menuCtrl.enable(true);
            this.events.publish({ name: 'app', value: 'ready' });
            // Navegar a home para usuarios logados
            if (this.router.url.indexOf('login') !== -1) {
              this.router.navigate(['home']);
            }
          }
        },
        error: (err) => {
          console.error(err);
          SplashScreen.hide();
          this.loading.hide();
        }
      });
  }

  public openMenu() {
    this.menuCtrl.open();
  }

  public openPage(page) {
    console.log('openPage', page);
    
    // Manejar secciones especiales (targetType: 'section')
    if (page.targetType === 'section') {
      this.openComponent(page);
      return;
    }
    
    if (!page.url && !page.pageId && !page.target) {
      console.log('Ninguna página asociada al elemento');
      return;
    }
    if (page.url) this.openLinkPage(page);
    else if (page.target) this.openComponent(page);
    else this.openIonicPage(page);
  }

  public openLinkPage(page) {
    console.log('openLinkPage');
    const paramsToAnalytics = { actions: 1 };
    const name = page.name ?? page.url;
    this.analyticsService.registerEvent(paramsToAnalytics, name);
    
    // Usar el sistema de LinksService para manejar todos los tipos de enlaces
    const link = {
      target: page.url,
      textToDisplay: page.title || page.name,
      targetType: page.linkType || 'url' // Fallback a 'url' si no se especifica tipo
    };
    
    const handlerLinkType = this.linksService.linkType[link.targetType];
    if (handlerLinkType) {
      handlerLinkType(link);
    } else {
      // Fallback: usar openInternalLink si el tipo no existe
      this.linksService.openInternalLink(page.url);
    }
  }

  private openIonicPage(page: any) {
    const paramsToAnalytics = { actions: 1 };
    const name = page.title || page.name || `Page ${page.pageId}`;
    const id = page.pageId ? parseInt(page.pageId) : undefined;
    this.analyticsService.registerEvent(paramsToAnalytics, name, id);
    
    const homePage = this.loginService.rescueSessionDataUser().rootPages.home;
    if (page.pageId === homePage.pageId) {
      this.router.navigate(['/home']);
    } else {
      this.router.navigate([`/generic/${page.pageId}`]);
    }
  }

  private openComponent(page) {
    const paramsToAnalytics = { actions: 1 };
    const name = page.textToDisplay || page.title || page.name || page.target;
    const id = page.id ? parseInt(page.id) : undefined;
    this.analyticsService.registerEvent(paramsToAnalytics, name, id);
    
    const handlerLinkType = this.linksService.linkType[page.targetType];
    handlerLinkType(page);
  }

  public async logout(allSessions = false) {
    this.loginService
      .logout(allSessions)
      .pipe(take(1))
      .subscribe({
        next: async (res) => {
          await this.clearSession();

          this.menuCtrl.close();
          this.menuCtrl.enable(false);

          if (Environment.CLIENT_ID === null) {
            this.router.navigate([this.loginPage]);
            return;
          }

          if (this.initialConfigService.opened.value === true) {
            this.initialConfigService.homePageLoaded = false;
            this.events.publish({ name: 'anonApp', value: 'expired' });
            this.anonymousLogin();
            return;
          }

          this.events.publish({ name: 'app', value: 'expired' });
          this.initialConfigService.homePageLoaded = false;
          console.log('al login');

          this.router.navigate([this.loginPage || 'login/first']);
        },
        error: (err) => console.log(err),

        complete: () => {
          this.loading.hide();
        }
      });
  }

  public async presentAlertLogOut() {
    const alertButtons: AlertButton[] = [
      {
        text: await this.languageService.translate('LOG-OUT-ALL-DEVICES'),
        handler: () => this.logout(true)
      },
      {
        text: await this.languageService.translate('ON-THIS-DEVICE'),
        handler: () => this.logout(false)
      },
      {
        text: await this.languageService.translate('CANCEL'),
        role: 'cancel'
      }
    ];

    const alertOptions: AlertOptions = {
      header: await this.languageService.translate('DO-YOU-WANT-TO-LOG-OUT'),
      buttons: alertButtons
    };

    let alert = await this.alertCtrl.create(alertOptions);

    await alert.present();
  }

  public subscribeNotifications() {
    this.events.notifications.pipe(filter(Boolean)).subscribe(async (notification: any | PushNotificationSchema | ActionPerformed) => {
      const notificationData = notification.data ?? notification.notification.data;

      if (notificationData.topic == 'chat' || notificationData.topic == 'bot') {
        this.goToChat(notificationData);
      } else {
        if (notificationData.target) {
          this.pushNotifications.openNotification(notificationData);
        } else {
          this.router.navigate(['/notifications']);
        }
      }
    });
  }

  private async goToChat(notification: any) {
    const currentUrl = this.router.url;

    if (!notification.idRoom) {
      this.toast.info('Corrupted notification');
      return;
    }

    if (notification.topic === 'bot') {
      if (currentUrl.includes(`chat/chatbot/${notification.idRoom}`)) {
        console.log('User already in conversation');
        return;
      }

      this.router.navigate([`chat/chatbot/${notification.idRoom}`]);
      return;
    }

    if (currentUrl.includes(`/chat/single/${notification.idRoom}`) || currentUrl.includes(`/chat/group/${notification.idRoom}`)) {
      console.log('User already in conversation');
      return;
    }

    const ref = await this.loginService.get('rooms/' + notification.idRoom);
    ref
      .snapshotChanges()
      .pipe(take(1))
      .subscribe(async (ref: any) => {
        const group = ref.payload.data();
        const parts = group.name.split('_');
        const single: boolean = parts.length == 2 && isNaN(parts[0]) === false && isNaN(parts[1]) === false;
        this.router.navigate([`chat/${single ? 'single' : 'group'}/${notification.idRoom}`]);
      });
  }

private async clearSession() {
    try {
      // Detener monitoreo de tokens
      this.loginService.stopTokenMonitoring();

      this.messageProvider.unsubscribeAll();
      this.woocommerceService.clearStoreUserInfoCache();
      await this.loginService.signOutFirebase();
      this.loginService.clearStorage();

      this.document.body.classList.remove('logged');
      this.document.body.classList.add('not-logged');
    } catch (e) {
      console.error(e);
    }
  }

  public async openInternalLink(url: string) {
    this.linksService.openInternalLink(url);
  }

  public changeViewStatus(selected) {
    const segment = selected.detail.value;
    if (this.buttonSelected === segment) return;
    this.buttonSelected = segment;
  }

  back() {
    window.history.back();
  }

/*   public subscribeDeepLinks() {
    App.addListener('appUrlOpen', (data: URLOpenListenerEvent) => {
      this.zone.run(() => {
        const url = new URL(data.url.toString());
        if (url.pathname !== null) {
          this.router.navigate([url.pathname]);
        }
      });
    });
  } */


    public subscribeDeepLinks() {
      console.log('🔗 Configurando deeplinks...');
      App.addListener('appUrlOpen', (data: URLOpenListenerEvent) => {
        console.log('🔗 Deeplink recibido:', data.url);
        this.zone.run(() => {
          const fullUrl = data.url.toString();
          console.log('🔗 URL completa:', fullUrl);
          
          // Extraer el path correctamente para custom URL schemes
          let pathname = '';
          if (fullUrl.startsWith(Environment.DEEPLINK_SCHEME + '://')) {
            // Para custom URL schemes, extraer todo después del esquema
            pathname = '/' + fullUrl.replace(Environment.DEEPLINK_SCHEME + '://', '');
          } else {
            // Para URLs normales, usar URL.pathname
            const url = new URL(fullUrl);
            pathname = url.pathname;
          }
          
          console.log('🔗 Pathname extraído:', pathname);
          
          if (pathname !== null && pathname !== '') {
            // Verificar si el usuario está logado
            const userData = this.loginService.autoLogin();
            const openedClient = this.clientService.isOpenedAccountClient();
            
            console.log('🔗 Usuario logado:', !!userData);
            console.log('🔗 Cliente abierto:', openedClient);
            console.log('🔗 Path solicitado:', pathname);
            
            // Si el path es de login y el usuario ya está logado, ir a home
            if (pathname.startsWith('/login') && userData) {
              console.log('🔗 Usuario logado intentando acceder a login, redirigiendo a home');
              this.router.navigate(['/home']);
              return;
            }
            
            // Si el usuario no está logado y no es una página de login, ir a la página inicial apropiada
            if (!userData && !pathname.startsWith('/login')) {
              console.log('🔗 Usuario no logado, navegando a:', this.initialPage);
              this.router.navigate([this.initialPage]);
              return;
            }
            
            // Si el usuario está logado o es una página de login, navegar al path del deeplink
            console.log('🔗 Navegando a:', pathname);
            
            // Manejar casos especiales de deeplinks
            let targetPath = pathname;
            
            // Si el path es solo un número, asumir que es un generic ID
            if (/^\/\d+$/.test(pathname)) {
              const id = pathname.substring(1); // Remover el slash inicial
              targetPath = `/generic/${id}`;
              console.log('🔗 Path convertido a generic:', targetPath);
            }
            
            // Manejar errores de routing
            this.router.navigate([targetPath]).catch((error) => {
              console.error('🔗 Error de routing:', error);
              console.log('🔗 Redirigiendo a home por error de routing');
              this.router.navigate(['/home']);
            });
          }
        });
      });
    }

  private subscribeLogoutRequest() {
    this.events.logout.pipe(filter((v) => v === 'request')).subscribe(() => {
      this.presentAlertLogOut();
    });
  }

  private getNotifications() {
    this.unreadNotifications$ = this.pushNotifications.getNotifications().pipe(
      map((res) => res.items.itemsNotViewedCount),
      shareReplay()
    );
  }

  private resolveSideMenuEnabled(): boolean {
    try {
      const stored = localStorage.getItem('userData');
      if (!stored) return true;
      const client = JSON.parse(stored)?.client;
      return client?.features?.sideMenu !== false;
    } catch {
      return true;
    }
  }

  private resolveNotificationsPanelEnabled(): boolean {
    try {
      const stored = localStorage.getItem('userData');
      if (!stored) return true;
      const client = JSON.parse(stored)?.client;
      return client?.features?.notificationsPanel !== false;
    } catch {
      return true;
    }
  }
}